import fp from 'lodash/fp';
import React, { useState, useCallback, useEffect, useMemo } from 'react';

import { useSizeContext, useMemoDeps } from 'Modules/ui';
import { useAction } from 'Modules/core/redux';
import { useTranslation } from 'Modules/core/i18n';
import {
    paginationActions,
    PaginationHeader,
    PaginationList,
    PaginationParametersControl,
    getDomainTypeFromPath,
    usePagination,
} from 'Modules/business/data';

import { UploadButton } from './UploadButton';
import { TransferButton } from './TransferButton';
import { Checkbox } from './Checkbox';
import { ListFilterField } from '../ListFilterField';
import { filterFieldForDomain } from '../../constants';
import styles from './index.module.scss';


export const PAGINATION_PARAMETERS = {
    documents: ({ BatchUId }) => ({
        filters: {
            batchuid: BatchUId,
        },
    }),
    registers: ({ Uid }) => ({
        filters: {
            register: Uid,
        },
    }),
    discountRegisters: ({ Uid }) => ({
        filters: {
            register: Uid,
        },
    }),
    banks: ({ Uid }) => ({
        filters: {
            bankUid: Uid,
        },
    }),
    orderAgreements: ({ Uid }) => ({
        filters: {
            orderAgreement: Uid,
        },
    }),
};

const getDefaultParameters = parametersModel => fp.flatten(parametersModel)[0];

const useRowHeight = rowHeightRem => {
    const { rem } = useSizeContext();
    const result = useMemo(
        () => fp.isFunction(rowHeightRem)
            ? fp.pipe(rowHeightRem, fp.multiply(rem))
            : rowHeightRem * rem,
        [rowHeightRem, rem],
    );
    return result;
};

const useListPagination = (path, modelParameters, omitFilters) => {
    const actualParameters = usePagination(path, 'parameters');
    const setParameters = useAction(
        parameters => paginationActions.setParameters(
            path,
            parameters,
        ),
        [path],
    );
    useEffect(
        () => {
            if (!fp.isEqual(fp.omit(omitFilters, actualParameters), modelParameters))
                setParameters(modelParameters);
        },
        [modelParameters, actualParameters, omitFilters, setParameters],
    );
};

const domainsWithTransferLayout = ['registers', 'discountRegisters'];

export const EntitysList = React.memo(({
    selectedId,
    onRowSelected,
    model,
    parametersModel,
    onModelChange,
    processTemplate,
    uploadPaginationPath,
    title,

    checkedRows,
    onCheckedChanged,
    classNames,
    checkedKeySelector = (_, index) => index,
}) => {
    const { t } = useTranslation('entitysList');

    const [{ key, placeholder }, changeKeyState] = useState(
        () => getDefaultParameters(parametersModel),
    );

    const {
        path,
        parameters,
        item,
        itemStyler,
        rowHeightRem,
        listProps,
        checkable,
        headerButton: HeaderButton,
    } = useMemo(
        () => model[key],
        [model, key],
    );

    const domain = useMemoDeps(getDomainTypeFromPath, [path]);
    const filterField = useMemo(
        () => filterFieldForDomain[domain],
        [domain],
    );

    useListPagination(
        path,
        parameters,
        `filters.${filterField}`,
    );

    const selectModel = useCallback(
        model => {
            if (onModelChange)
                onModelChange(model);
            changeKeyState(model);
        },
        [onModelChange],
    );

    const elementRenderer = useCallback(
        ({ data, ...props }) => {
            const Item = item;
            return (
                <Item
                    data={data}
                    itemStyler={itemStyler}
                    checkable={checkable}
                    checkedRows={checkedRows}
                    onCheck={onCheckedChanged}
                    checkedKeySelector={checkedKeySelector}
                    {...props}
                />
            );
        },
        [
            item,
            itemStyler,
            checkable,
            checkedKeySelector,
            checkedRows,
            onCheckedChanged,
        ],
    );

    const placeholderText = useMemo(
        () => placeholder ? t(placeholder) : undefined,
        [placeholder, t],
    );

    const rowHeight = useRowHeight(rowHeightRem);

    const onUpload = useCallback(
        () => selectModel(fp.find(({ key }) => key === 'documents', parametersModel)),
        [selectModel, parametersModel],
    );

    return (
        <div className={styles.list}>
            <PaginationHeader
                updateAnytime
                path={path}
                title={title}
                buttons={domainsWithTransferLayout.includes(domain) && TransferButton}
            />
            {uploadPaginationPath && (
                <UploadButton
                    paginationPath={uploadPaginationPath}
                    processTemplate={processTemplate}
                    onUpload={onUpload}
                />
            )}
            {checkable && (
                <Checkbox
                    paginationPath={path}
                    checkedRows={checkedRows}
                    onChange={onCheckedChanged}
                    className={styles.checkbox}
                />
            )}
            <PaginationParametersControl
                className={classNames && classNames.paginationControl}
                options={parametersModel}
                value={key}
                onChange={selectModel}
            />
            {filterField && (
                <ListFilterField
                    key={`${path}-${filterField}`}
                    path={path}
                    field={filterField}
                />
            )}
            {HeaderButton && <HeaderButton path={path} />}
            <PaginationList
                className={styles.container}
                path={path}
                selectedId={selectedId}
                itemData={checkable && { checkedRows, selectedId }}
                onRowSelected={onRowSelected}
                elementRenderer={elementRenderer}
                rowHeight={rowHeight}
                placeholderText={placeholderText}
                {...listProps}
            />
        </div>
    );
});
